import React from 'react'
import { Tabs, Input } from 'antd'
import cx from 'classnames'
import { SearchOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { IListItem } from '../../../types/interfaces'

import { RootState } from '../../../reducers'

// enums
import { DEFAULT_BUTTON_HEIGHT, DEFAULT_OBJECT_STATUS_WIDTH, DROPPING_ELEMENT, ELEMENT_TYPES, NUMBER_OF_ROWS, OBJECTS } from '../../../utils/enums'

import { objectList } from './elementsList'

// assets
import { ReactComponent as DragIcon } from '../../../assets/icons/BaseIcons/drag-icon.svg'
import { ReactComponent as TextIcon } from '../../../assets/icons/ElementIcons/text-icon.svg'
import { ReactComponent as InputIcon } from '../../../assets/icons/ElementIcons/input-icon.svg'
import { ReactComponent as DropDownIcon } from '../../../assets/icons/ElementIcons/dropdown-icon.svg'
import { ReactComponent as CheckBoxIcon } from '../../../assets/icons/ElementIcons/checkbox-icon.svg'
import { ReactComponent as RadioIcon } from '../../../assets/icons/ElementIcons/radio-icon.svg'
import { ReactComponent as TextareaIcon } from '../../../assets/icons/ElementIcons/textarea-icon.svg'
import { ReactComponent as HorizontalLineIcon } from '../../../assets/icons/ElementIcons/horizontal-line-icon.svg'
import { ReactComponent as DateIcon } from '../../../assets/icons/ElementIcons/date-icon.svg'
import { ReactComponent as CalendarIcon } from '../../../assets/icons/ElementIcons/calendar-icon.svg'
import { ReactComponent as ButtonIcon } from '../../../assets/icons/ElementIcons/button-icon.svg'
import { ReactComponent as TableIcon } from '../../../assets/icons/ElementIcons/table-icon.svg'
import { ReactComponent as HeadingIcon } from '../../../assets/icons/ElementIcons/heading-icon.svg'
import { ReactComponent as TabIcon } from '../../../assets/icons/ElementIcons/section-icon.svg'
import { ReactComponent as ImageIcon } from '../../../assets/icons/ElementIcons/image-icon.svg'
import { ReactComponent as SelectFromTableIcon } from '../../../assets/icons/ElementIcons/webview-icon.svg'

// reducers
import { FORM_PLACEHOLDER } from '../../../reducers/form/formTypes'

const { TabPane } = Tabs

type Props = {
	itemList: IListItem[]
	filterList: Function
}

const ItemListLeftSideBar = (props: Props) => {
	const { itemList, filterList } = props
	const dispatch: any = useDispatch()
	const formItemCopy = useSelector((state: RootState) => state.formTemplate.formItemCopy)

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		filterList(event.currentTarget.value)
	}

	const renderIcon = (type: string) => {
		switch (type) {
			case ELEMENT_TYPES.TEXT:
				return <TextIcon width={20} height={20} />
			case ELEMENT_TYPES.INPUT:
				return <InputIcon width={20} height={20} />
			case ELEMENT_TYPES.DROPDOWN_BUTTON:
				return <DropDownIcon width={20} height={20} />
			case ELEMENT_TYPES.CHECK_BOX:
				return <CheckBoxIcon width={20} height={20} />
			case ELEMENT_TYPES.RADIO_BUTTON:
				return <RadioIcon width={20} height={20} />
			case ELEMENT_TYPES.TEXT_AREA:
				return <TextareaIcon width={20} height={20} />
			case ELEMENT_TYPES.HORIZONTAL_LINE:
				return <HorizontalLineIcon width={20} height={20} />
			case ELEMENT_TYPES.DATE_PICKER:
				return <DateIcon width={20} height={20} />
			case ELEMENT_TYPES.CALENDAR:
				return <CalendarIcon width={20} height={20} />
			case ELEMENT_TYPES.BUTTON:
				return <ButtonIcon width={20} height={20} />
			case ELEMENT_TYPES.TABLE:
				return <TableIcon width={20} height={20} />
			case ELEMENT_TYPES.TITLE:
				return <HeadingIcon width={20} height={20} />
			case ELEMENT_TYPES.TAB_BOX:
				return <TabIcon width={20} height={20} />
			case ELEMENT_TYPES.IMAGE:
				return <ImageIcon width={20} height={20} className={'icon--stroked'} />
			case ELEMENT_TYPES.SELECT_FROM_TABLE:
				return <SelectFromTableIcon width={20} height={20} />
			default:
				return undefined
		}
	}

	// set placeholder height based on from item type
	const setPlaceholderHeight = (type?: string) => {
		let height = 0
		if (type === ELEMENT_TYPES.BUTTON) {
			height = DEFAULT_BUTTON_HEIGHT
		} else if (type === ELEMENT_TYPES.TEXT) {
			height = NUMBER_OF_ROWS.BUTTON_S_SIZE
		} else if (type === ELEMENT_TYPES.TITLE) {
			height = NUMBER_OF_ROWS.BUTTON_S_SIZE
		} else if (type === ELEMENT_TYPES.HORIZONTAL_LINE) {
			height = NUMBER_OF_ROWS.HORIZONTAL_LINE
		} else if (type === ELEMENT_TYPES.SELECT_FROM_TABLE) {
			height = NUMBER_OF_ROWS.SELECT_FROM_TABLE
		} else {
			height = DROPPING_ELEMENT.HEIGHT
		}

		let width = 0
		if (type === OBJECTS.OBJECT_STATUS) {
			width = DEFAULT_OBJECT_STATUS_WIDTH
		} else {
			width = DROPPING_ELEMENT.WIDTH
		}
		dispatch({ type: FORM_PLACEHOLDER.FORM_PLACEHOLDER_WIDTH, data: width })
		dispatch({ type: FORM_PLACEHOLDER.FORM_PLACEHOLDER_HEIGHT, data: height })
	}

	const renderDragItem = (items: any[]) =>
		items.map((item: any) => {
			const hideIconClass = cx({
				'hide-element': item?.key === OBJECTS.OBJECT_STATUS
			})
			const withoutIconClass = cx({
				'without-icon': item?.key === OBJECTS.OBJECT_STATUS
			})
			return (
				<div key={item?.key || item?.type} className={'droppable-element-list-item'}>
					<div className='droppable-element' draggable={false}>
						<div
							className={'drag-area'}
							draggable={true}
							onDragStart={(event) => {
								setPlaceholderHeight(item?.key || item?.type)
								event.dataTransfer.setData('dropItem', JSON.stringify(item))
							}}
						>
							<DragIcon />
						</div>
						<ul className={`ul-droppable-element select-none ${hideIconClass}`}>
							<div className='droppable-element-icon-box'>{item?.icon || renderIcon(item?.type || '')}</div>
						</ul>
						<ul className={`ul-droppable-element select-none ${withoutIconClass}`}>
							<li>
								<div className='droppable-element-title'>{item?.label || item?.formItemGeneral?.label}</div>
							</li>
							<li>
								<div className='droppable-element-description'>{item?.description || item?.type}</div>
							</li>
						</ul>
					</div>
				</div>
			)
		})

	return (
		<div className='droppable-element-list-menu'>
			<Input className='list-search-input' placeholder='Search elements...' prefix={<SearchOutlined />} onChange={handleChange} />
			<Tabs defaultActiveKey='1' centered>
				<TabPane tab='Basic' key={'1'}>
					{renderDragItem(itemList)}
				</TabPane>
				<TabPane tab='Objects' key={'2'}>
					{formItemCopy && renderDragItem([formItemCopy])}
					{renderDragItem(objectList)}
				</TabPane>
			</Tabs>
		</div>
	)
}

export default React.memo(ItemListLeftSideBar)
